import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import theme from './theme/theme';
import AppLayout from './components/AppLayout';
import DashboardPage from './pages/DashboardPage';
import WorkItemsPage from './pages/WorkItemsPage';
import CalendarPage from './pages/CalendarPage';
import StrategyPage from './pages/StrategyPage';
import ImagesPage from './pages/ImagesPage';
import ContextPage from './pages/ContextPage';
import PreferencesPage from './pages/PreferencesPage';
import FocusTimePage from './pages/FocusTimePage';
import ReportsPage from './pages/ReportsPage';
import { TranscriptsPage } from './pages/TranscriptsPage';
import EAWorkspacePage from './pages/EAWorkspacePage';
import PromptManagementPage from './pages/PromptManagementPage';
import AdminPage from './pages/AdminPage';
import PlatformOverviewPage from './pages/PlatformOverviewPage';
import PlatformTenantsPage from './pages/PlatformTenantsPage';
import PlatformProductsPage from './pages/PlatformProductsPage';
import PlatformEmailPage from './pages/PlatformEmailPage';
import PlatformSecurityPage from './pages/PlatformSecurityPage';
import TenantBillingPage from './pages/TenantBillingPage';
import SignupPage from './pages/SignupPage';
import OnboardingSuccessPage from './pages/OnboardingSuccessPage';
import OAuthCallback from './auth/OAuthCallback';
import MfaSetupPage from './pages/MfaSetupPage';

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, ensureValidToken } = useAuth();

  useEffect(() => {
    setTokenGetter(ensureValidToken);
  }, [ensureValidToken]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
}

// Route "/" based on user role
function RoleBasedHome() {
  const { user } = useAuth();
  if (user?.role === 'PlatformAdmin') {
    return <PlatformOverviewPage />;
  }
  return <DashboardPage />;
}

function ProtectedApp() {
  return (
    <AuthProvider>
      <AuthGate>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<RoleBasedHome />} />
            <Route path="/mfa-setup" element={<MfaSetupPage />} />
            <Route path="/focus" element={<FocusTimePage />} />
            <Route path="/work-items" element={<WorkItemsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/strategy" element={<StrategyPage />} />
            <Route path="/images" element={<ImagesPage />} />
            <Route path="/transcripts" element={<TranscriptsPage />} />
            <Route path="/context" element={<ContextPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/ea-workspace" element={<EAWorkspacePage />} />
            <Route path="/prompt-management" element={<PromptManagementPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/billing" element={<TenantBillingPage />} />
            <Route path="/platform" element={<PlatformOverviewPage />} />
            <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
            <Route path="/platform/products" element={<PlatformProductsPage />} />
            <Route path="/platform/email" element={<PlatformEmailPage />} />
            <Route path="/platform/security" element={<PlatformSecurityPage />} />
          </Route>
          {/* Catch /login and any unknown routes — redirect to dashboard */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthGate>
    </AuthProvider>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function AppRouter() {
  const pathname = window.location.pathname;

  // Public routes — render WITHOUT auth
  if (pathname === '/signup' || pathname.startsWith('/onboarding') || pathname === '/auth/callback') {
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/onboarding/success" element={<OnboardingSuccessPage />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
      </Routes>
    );
  }

  // Everything else — protected
  return <ProtectedApp />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
